import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { PositionsScreen } from '@/features/trading/positions-screen';
import { positionDetailQuery, positionsQuery, tradesQuery } from '@/features/trading/trading-api';
import { meQuery } from '@/lib/queries';

const objectId = z
  .string()
  .regex(/^[a-f0-9]{24}$/)
  .optional()
  .catch(undefined);

export const Route = createFileRoute('/_app/positions')({
  validateSearch: z.object({
    /** Open (the default, left out of the URL), Closed, or History (every trade). */
    view: z.enum(['open', 'closed', 'history']).optional().catch(undefined),
    /** The position sheet. */
    id: objectId,
    /** The trade sheet. */
    trade: objectId,
    /** @deprecated Old links (`?status=closed`); redirected to `view`. */
    status: z.enum(['open', 'closed']).optional().catch(undefined),
  }),
  beforeLoad: ({ search }) => {
    if (search.status !== undefined || search.view === 'open') {
      const { status, ...rest } = search;
      const view = search.view ?? status;
      throw redirect({
        to: '/positions',
        search: { ...rest, view: view === 'open' ? undefined : view },
        replace: true,
      });
    }
  },
  loaderDeps: ({ search }) => ({ view: search.view ?? 'open', id: search.id, trade: search.trade }),
  loader: async ({ context: { queryClient }, deps }) => {
    const me = await queryClient.ensureQueryData(meQuery);
    const mode = me.settings.executionMode;
    // The open list feeds the Open count and "Sell all" on every view.
    void queryClient.prefetchInfiniteQuery(positionsQuery('open', mode));
    if (deps.view === 'closed') void queryClient.prefetchInfiniteQuery(positionsQuery('closed', mode));
    if (deps.view === 'history' || deps.trade) void queryClient.prefetchInfiniteQuery(tradesQuery);
    if (deps.id) void queryClient.prefetchQuery(positionDetailQuery(deps.id));
  },
  component: PositionsRoute,
});

function PositionsRoute() {
  const { view = 'open', id, trade } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  // Opening a sheet from this screen pushes one history entry, so Back closes it; closing it then
  // steps back over that entry. Switching between the two sheets replaces it. Opened straight from
  // a URL (reload, a shared link), closing just drops the param.
  const pushed = useRef(false);
  const sheetOpen = id !== undefined || trade !== undefined;
  useEffect(() => {
    if (!sheetOpen) pushed.current = false;
  }, [sheetOpen]);

  const openSheet = (next: { id?: string; trade?: string }) => {
    const replace = sheetOpen;
    if (!replace) pushed.current = true;
    void navigate({ search: (prev) => ({ ...prev, id: next.id, trade: next.trade }), replace });
  };
  const closeSheet = () => {
    if (pushed.current) {
      pushed.current = false;
      router.history.back();
    } else {
      void navigate({ search: (prev) => ({ ...prev, id: undefined, trade: undefined }), replace: true });
    }
  };

  return (
    <PositionsScreen
      view={view}
      positionId={id}
      tradeId={trade}
      onView={(next) =>
        void navigate({
          search: (prev) => ({ ...prev, view: next === 'open' ? undefined : next }),
          replace: true,
        })
      }
      onPosition={(next) => (next ? openSheet({ id: next }) : closeSheet())}
      onTrade={(next) => (next ? openSheet({ trade: next }) : closeSheet())}
    />
  );
}
